/**
 * « Is the focus inside a field? » - the one guard that makes an editor-wide
 * keyboard shortcut safe (issue #135, reused as-is by #136).
 *
 * ## The rule, and why it is a rule rather than a condition
 *
 * `Ctrl+Z` inside an `input`, a `textarea`, a `select` or a `contentEditable`
 * belongs to the BROWSER: it walks back word by word through what somebody just
 * typed, with a caret and a selection nothing in React can reproduce. Outside a
 * field, that same chord has no meaning at all, which is the room the editor's
 * own undo stack takes. So the shortcut is inert exactly while a field has the
 * focus - and the day #136 hangs `Suppr` on the same window it needs the SAME
 * answer, because a `Suppr` that deletes the selected step while somebody is
 * erasing a character is the same defect with a worse outcome.
 *
 * Written once, here, rather than twice: two spellings of « am I in a field »
 * would drift, and the half that drifts is always the one nobody is looking at.
 *
 * ## It reads the EVENT's target, not `document.activeElement`
 *
 * A keydown is dispatched at the focused element and bubbles to the window, so
 * the target IS the field. `document.activeElement` answers the same thing
 * almost always and differently in the cases that matter: inside a shadow root
 * it answers the host, and between a blur and the next focus it answers
 * `<body>` - which would let a chord through in the exact frame a field is
 * handing the focus to another.
 *
 * ## Three things it deliberately does NOT do
 *
 *   - It does not narrow by input TYPE. A checkbox and a radio have no native
 *     undo, so letting the chord through on them would be defensible; but the
 *     rule would then be « fields, except these, and mind the ones added later »,
 *     and a guard nobody can state in one sentence is a guard that gets it
 *     wrong. Coarse and sayable beats exact and forgotten.
 *   - It does not read `disabled` or `readonly`. Neither can hold the focus in
 *     a way that matters here: a disabled control receives no key events at all,
 *     and a readonly input still owns its caret and its selection.
 *   - It does not walk out of an iframe. Nothing in this application embeds one,
 *     and a guard that answered for a document it cannot see would be guessing.
 */

/** The three form elements the browser gives an editing behaviour of its own. */
const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/**
 * True when a keyboard event was aimed at something that edits text itself.
 *
 * Hand it `event.target` from a window-level keydown; a listener bound to a
 * node deeper in the tree gets the same answer for the same reason.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (EDITABLE_TAGS.has(target.tagName)) return true;
  /*
    `isContentEditable` is the honest reading - it is true on a descendant of a
    contentEditable host as well as on the host - and it is also the one jsdom
    does not implement, where it reads `undefined`. So the attribute walk below
    stands beside it rather than behind it: the property answers in a browser,
    the walk answers in a test, and both answer the same thing.

    `:not([contenteditable="false"])` is load-bearing: the rich text field and
    the painted field of this repository both render `contentEditable={false}`
    in read-only, which writes the attribute rather than dropping it.
  */
  if (target instanceof HTMLElement && target.isContentEditable) return true;
  return target.closest('[contenteditable]:not([contenteditable="false"])') !== null;
}
